import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

import { TellentAppsDataService } from '@core/tellent-data';
import { TellentHttpApiService } from '@core/tellent-http-legacy';
import { PositionSkillEntity } from '@core/tellent-skills-types';
import { SeatKind } from '@core/tellent-types';

@Injectable({ providedIn: 'root' })
export class PositionSkillsHttpClient {
  private readonly httpApi = inject(TellentHttpApiService);
  private readonly growApp$ = inject(TellentAppsDataService).getAppBySeatKind(SeatKind.Grow);

  public fetchPositionSkills(positionGuid: string): Observable<PositionSkillEntity> {
    return this.growApp$.pipe(
      take(1),
      switchMap(app =>
        this.httpApi.get(`/positions/app/${app.guid}/positions/${positionGuid}/skills`, null, {
          skipCompany: true,
        }),
      ),
    );
  }

  public assignSkill(
    positionGuid: string,
    skillId: number,
    proficiencyId: number,
  ): Observable<string> {
    return this.growApp$.pipe(
      take(1),
      switchMap(app =>
        this.httpApi.post(
          `/positions/app/${app.guid}/positions/${positionGuid}/skills/assign`,
          null,
          { skillId, proficiencyId },
          { skipCompany: true },
        ),
      ),
    );
  }

  public unassignSkill(positionGuid: string, skillId: number): Observable<string> {
    return this.growApp$.pipe(
      take(1),
      switchMap(app =>
        this.httpApi.post(
          `/positions/app/${app.guid}/positions/${positionGuid}/skills/unassign`,
          null,
          { skillId },
          { skipCompany: true },
        ),
      ),
    );
  }

  public unassignSkillMapping(
    positionGuid: string,
    skillMappingId: number,
    levelId: number,
  ): Observable<string> {
    return this.growApp$.pipe(
      take(1),
      switchMap(app =>
        this.httpApi.post(
          `/positions/app/${app.guid}/positions/${positionGuid}/skill-mappings/unassign`,
          null,
          { skillMappingId, levelId },
          { skipCompany: true },
        ),
      ),
    );
  }

  public updateSkill(
    positionGuid: string,
    skillId: number,
    proficiencyId: number,
  ): Observable<string> {
    return this.growApp$.pipe(
      take(1),
      switchMap(app =>
        this.httpApi.post(
          `/positions/app/${app.guid}/positions/${positionGuid}/skills/update`,
          null,
          { skillId, proficiencyId },
          { skipCompany: true },
        ),
      ),
    );
  }

  public assignSkillMapping(
    positionGuid: string,
    skillMappingId: number,
    levelId: number,
  ): Observable<string> {
    return this.growApp$.pipe(
      take(1),
      switchMap(app =>
        this.httpApi.post(
          `/positions/app/${app.guid}/positions/${positionGuid}/skill-mappings/assign`,
          null,
          { skillMappingId, levelId },
          { skipCompany: true },
        ),
      ),
    );
  }
}
